import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, EditPane, type EditPaneTab } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  currencyFromId,
  PartClient,
  totalQuantity,
  type PartDTO,
  type PartQuantityDTO,
  type PartSupplierDTO,
  type PartTransactionDTO,
  type PurchaseOrderLineDTO,
} from "../../api/part";

const round2 = (value: number | null | undefined) =>
  value === null || value === undefined ? null : Math.round(value * 100) / 100;

function QuantityGrid({ part }: { part: PartDTO }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<PartQuantityDTO>();
    return [
      c.text("warehouse", (row) => row.warehouse_no ?? "", { headerKey: "column.warehouse", priority: 1 }),
      c.number("quantity", (row) => row.real_qty ?? 0, { headerKey: "column.quantity", priority: 2 }),
      c.number("avg_cost", (row) => round2(row.avg_cost), { headerKey: "column.avg-cost" }),
      c.number("last_cost", (row) => round2(row.last_cost), { headerKey: "column.last-cost" }),
    ];
  }, []);
  // Vaadin sorts this grid by quantity descending by default.
  const rows = useMemo(
    () => [...(part.part_quantities ?? [])].sort((a, b) => (b.real_qty ?? 0) - (a.real_qty ?? 0)),
    [part],
  );
  return (
    <DataTable data={rows} columns={cols} getRowId={(row) => String(row.id)} emptyMessage={t("table.empty", "Nothing to show")} />
  );
}

function SupplierGrid({ part }: { part: PartDTO }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<PartSupplierDTO>();
    return [
      c.number("supplier_id", (row) => row.supplier?.id ?? null, {
        headerKey: "column.supplier.id",
        defaultVisible: false,
      }),
      c.text("supplier_no", (row) => row.supplier?.no ?? "", { headerKey: "column.supplier.no", priority: 1 }),
      c.text("supplier_name", (row) => row.supplier?.name ?? "", { headerKey: "column.supplier.name", priority: 2 }),
      c.text("supplier_part_no", (row) => row.supplier_part_no ?? "", {
        headerKey: "column.supplier.part-no",
        defaultVisible: false,
      }),
      c.number("price", (row) => round2(row.supplier_price), { headerKey: "column.supplier.price", priority: 3 }),
      c.text("currency", (row) => currencyFromId(row.supplier?.currency), {
        headerKey: "column.supplier.currency",
        filter: "select",
      }),
      c.custom(
        "default",
        (row) => (row.idx === part.default_supplier_idx ? "yes" : "no"),
        (row) =>
          row.idx === part.default_supplier_idx ? (
            <Check className="size-4 text-ok" />
          ) : (
            <span />
          ),
        { headerKey: "column.default", filter: "select" },
      ),
      c.custom(
        "active",
        (row) => (row.supplier?.active ? "ACTIVE" : "INACTIVE"),
        (row) => (
          <Badge variant={row.supplier?.active ? "success" : "error"} size="sm">
            {row.supplier?.active ? "ACTIVE" : "INACTIVE"}
          </Badge>
        ),
        { headerKey: "column.active", filter: "select" },
      ),
    ];
  }, [part]);
  return (
    <DataTable
      data={part.suppliers ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

function PurchaseOrderGrid({ part }: { part: PartDTO }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<PurchaseOrderLineDTO>();
    // Missing = ordered − received − cancelled; the DTO doesn't carry it.
    const missing = (row: PurchaseOrderLineDTO) =>
      (row.line_purchase_qty ?? 0) - (row.line_received_qty ?? 0) - (row.line_cancel_qty ?? 0);
    return [
      c.custom(
        "supplier",
        (row) => row.supplier?.name ?? "",
        (row) => (
          <span>
            {row.supplier?.name}
            <span className="ml-1 text-[11px] text-ink-faint">{row.supplier?.no}</span>
          </span>
        ),
        { headerKey: "column.supplier", priority: 1 },
      ),
      c.text("user", (row) => row.user ?? "", { headerKey: "column.user", defaultVisible: false }),
      c.text("warehouse", (row) => row.warehouse?.no ?? "", { headerKey: "column.warehouse" }),
      c.text("order_no", (row) => String(row.no ?? ""), { headerKey: "column.order.no", priority: 2 }),
      c.text("reference", (row) => row.reference ?? "", {
        headerKey: "column.supplier.order-no",
        defaultVisible: false,
      }),
      c.text("order_date", (row) => row.puh_date ?? "", {
          headerKey: "column.order.date",
          filter: "daterange",
          priority: 3,
        }),
      c.text("req_date", (row) => row.req_date ?? "", {
          headerKey: "column.order.req-date",
          filter: "daterange",
          defaultVisible: false,
        }),
      c.text("init_rcvd", (row) => row.line_init_rcvg_dt ?? "", {
        headerKey: "column.init-rcvd-date",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.text("currency", (row) => currencyFromId(row.currency), {
        headerKey: "column.order.currency",
        filter: "select",
      }),
      c.number("subtotal", (row) => round2(row.subtotal), { headerKey: "column.order.subtotal", defaultVisible: false }),
      c.number("tax_1", (row) => round2(row.tax1), {
        headerKey: "column.order.tax-1",
        defaultVisible: false,
      }),
      c.number("tax_2", (row) => round2(row.tax2), {
        headerKey: "column.order.tax-2",
        defaultVisible: false,
      }),
      c.number("total", (row) => round2(row.total), { headerKey: "column.order.total" }),
      c.text("psp_part_no", (row) => row.psp_part_no ?? "", {
        headerKey: "column.supplier.part-no",
        defaultVisible: false,
      }),
      c.number("psp_price", (row) => round2(row.psp_price), { headerKey: "column.supplier.price" }),
      c.number("purchased", (row) => row.line_purchase_qty ?? 0, { headerKey: "column.qty-purchased", priority: 3 }),
      c.number("received", (row) => row.line_received_qty ?? 0, { headerKey: "column.qty-received" }),
      c.number("cancelled", (row) => row.line_cancel_qty ?? 0, { headerKey: "column.qty-cancel", defaultVisible: false }),
      c.number("missing", (row) => missing(row), { headerKey: "column.qty-missing" }),
    ];
  }, []);
  return (
    <DataTable
      data={part.purchase_orders ?? []}
      columns={cols}
      getRowId={(row) => `${row.id}-${row.line_id ?? 0}`}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

function TransactionGrid({ part }: { part: PartDTO }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<PartTransactionDTO>();
    return [
      c.number("id", (row) => row.id, { headerKey: "column.id", defaultVisible: false }),
      c.text("no", (row) => String(row.no ?? ""), { headerKey: "column.no", priority: 2 }),
      c.text("type", (row) => String(row.type ?? ""), { headerKey: "column.type", filter: "select" }),
      c.text("date", (row) => row.date ?? "", {
          headerKey: "column.date",
          filter: "daterange",
          priority: 1,
        }),
      c.text("timestamp", (row) => row.timestamp ?? "", { headerKey: "column.timestamp", defaultVisible: false }),
      c.text("description", (row) => row.description ?? "", { headerKey: "column.description", priority: 3 }),
      c.text("warehouse", (row) => row.warehouse_no ?? "", { headerKey: "column.warehouse" }),
      c.number("qty_before", (row) => row.qty_before ?? 0, { headerKey: "column.qty-before", defaultVisible: false }),
      c.number("qty", (row) => row.transaction_qty ?? 0, { headerKey: "column.transaction.qty", priority: 3 }),
      c.number("qty_after", (row) => row.qty_after ?? 0, { headerKey: "column.qty-after" }),
      c.number("total_cost", (row) => round2(row.total_cost), { headerKey: "column.total-cost" }),
      c.custom(
        "inventory_value",
        (row) => round2((row.qty_after ?? 0) * (row.total_cost ?? 0)) ?? 0,
        (row) => (
          <span className="tabular">{((row.qty_after ?? 0) * (row.total_cost ?? 0)).toFixed(2)} $</span>
        ),
        { headerKey: "column.inventory-value", defaultVisible: false },
      ),
      c.text("user", (row) => row.user ?? "", { headerKey: "column.user", defaultVisible: false }),
    ];
  }, []);
  return (
    <DataTable
      data={part.transactions ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

/**
 * One part's detail — quantities, suppliers, purchase orders, transactions —
 * from a single `/part/full/{id}` fetch (Vaadin fetched it twice, once per
 * lazy tab; one fetch covers all four here).
 */
export function PartDetailPage({ partId }: { partId: number }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const parts = useMemo(() => new PartClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["part", "full", partId],
    queryFn: () => parts.full(partId),
    staleTime: 60_000,
  });

  const tabs: EditPaneTab[] = data
    ? [
        {
          id: "quantity",
          flush: true,
          label: t("tab.part.quantity", "Quantity"),
          badge: (data.part_quantities ?? []).length,
          content: <QuantityGrid part={data} />,
        },
        {
          id: "suppliers",
          flush: true,
          label: t("tab.part.suppliers", "Suppliers"),
          badge: (data.suppliers ?? []).length,
          content: <SupplierGrid part={data} />,
        },
        {
          id: "orders",
          flush: true,
          label: t("tab.part.orders", "Orders"),
          badge: (data.purchase_orders ?? []).length,
          content: <PurchaseOrderGrid part={data} />,
        },
        {
          id: "transactions",
          flush: true,
          label: t("tab.part.transactions", "Transactions"),
          badge: (data.transactions ?? []).length,
          content: <TransactionGrid part={data} />,
        },
      ]
    : [];

  return (
    <>
      <PageHeader
        parent={{ to: "/parts", label: t("navigation.item.parts", "Parts") }}
        title={`${data?.no ?? partId}`}
        subtitle={data?.description1 ?? undefined}
        badges={
          data ? (
            <>
              {data.part_group && (
                <Badge variant="neutral" size="sm">
                  {data.part_group.no} - {data.part_group.description}
                </Badge>
              )}
              <Badge variant="info" size="sm">
                {t("parts.available-qty", "Available qty")}: {totalQuantity(data)}
              </Badge>
            </>
          ) : null
        }
      />
      {isLoading ? (
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">{t("common.loading", "Loading…")}</div>
      ) : data ? (
        <EditPane tabs={tabs} urlParam="tab" />
      ) : (
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">{t("table.empty", "Nothing to show")}</div>
      )}
    </>
  );
}
